// Lib
import React, { CSSProperties, RefObject } from 'react';
import { useSelector } from 'react-redux';
import { EditorToolbar } from '@milanote/roma/application';
import { cx } from '@milanote/roma/utils';

// Utils
import { stopPropagationOnly } from '../../../../../utils/domUtil';

// Selectors
import { currentBoardHasMediaDownloadAccessSelector } from '../../../../../utils/permissions/elementPermissionsSelector';
import { isPresentationModeEnabledSelector } from '../../../../../workspace/presentation/presentationSelector';

// Components
import ImageModalV2PrimaryTools from './ImageModalV2PrimaryTools';
import StickyPortalMount from '../../../../../components/animations/StickyPortalMount';

// Types
import { ImageModalV2Mode } from '../hooks/useImageModalV2Mode';
import { ImMNElement } from '../../../../../../common/elements/elementModelTypes';

// Constants
import { IMAGE_MODAL_SECONDARY_TOOLBAR_ID } from '../../imageModalConstants';

// Styles
import './ImageModalV2Toolbar.scss';

type ImageModalV2ToolbarProps = {
    element: ImMNElement;
    mode: ImageModalV2Mode;
    isEditable: boolean;
    canNavigate: boolean;
    navigateToPreviousImage: () => void;
    navigateToNextImage: () => void;
    setMode: (mode: ImageModalV2Mode) => void;
    isToolPanelOpen: boolean;
    isVisible?: boolean;
    style?: CSSProperties;
    primaryToolbarRef?: RefObject<HTMLDivElement>;
};

const ImageModalV2Toolbar: React.FC<ImageModalV2ToolbarProps> = ({
    element,
    mode,
    isEditable,
    canNavigate,
    navigateToPreviousImage,
    navigateToNextImage,
    setMode,
    isToolPanelOpen,
    isVisible = true,
    style,
    primaryToolbarRef,
}) => {
    const showDownloadOption = useSelector(currentBoardHasMediaDownloadAccessSelector);
    const isPresentationMode = useSelector(isPresentationModeEnabledSelector);
    const showPrimary = !isToolPanelOpen;
    const toolbarStyle = {
        ...style,
        bottom: showPrimary ? 0 : style?.bottom,
    };

    return (
        <div
            className={cx(
                'ImageModalV2Toolbar absolute right-0 left-0 z-[2] w-full pointer-events-none transition-opacity duration-[120ms] ease-out',
                showPrimary ? 'h-10' : 'h-12',
                !isVisible && 'opacity-0 [&_*]:!pointer-events-none',
            )}
            style={toolbarStyle}
        >
            <div className={cx('ImageModalV2Toolbar-animation-container', 'relative w-full')}>
                <EditorToolbar
                    ref={primaryToolbarRef}
                    isOpen={showPrimary}
                    autoFocus={false}
                    restoreFocus={false}
                    className={cx(
                        'ImageModalV2Toolbar-primary !absolute !top-0 !bottom-auto left-1/2 flex h-10 w-full items-center justify-center pointer-events-none',
                    )}
                >
                    <ImageModalV2PrimaryTools
                        element={element}
                        mode={mode}
                        isEditable={isEditable}
                        showDownloadOption={showDownloadOption}
                        isPresentationMode={isPresentationMode}
                        canNavigate={canNavigate}
                        navigateToPreviousImage={navigateToPreviousImage}
                        navigateToNextImage={navigateToNextImage}
                        setMode={setMode}
                    />
                </EditorToolbar>
                <EditorToolbar
                    isOpen={!showPrimary}
                    autoFocus={false}
                    restoreFocus={false}
                    className="ImageModalV2Toolbar-secondary !absolute !top-0 !bottom-auto left-1/2 flex h-12 w-full items-end justify-center pointer-events-none"
                >
                    <StickyPortalMount
                        id={IMAGE_MODAL_SECONDARY_TOOLBAR_ID}
                        className="ImageModalV2SecondaryTools image-secondary-tools"
                        onMouseDown={stopPropagationOnly}
                        onClick={stopPropagationOnly}
                    />
                </EditorToolbar>
            </div>
        </div>
    );
};

export default ImageModalV2Toolbar;
